import { memo, useMemo, useRef } from 'react';

import useMediaQuery from '@mui/material/useMediaQuery';
import Chip from '@mui/material/Chip';
import Drawer from '@mui/material/Drawer';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

// project imports
import MenuCard from './MenuCard';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';

import { MenuOrientation } from 'config';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';
import SimpleBar from 'ui-component/third-party/SimpleBar';
import useAppSettings from 'hooks/useAppSettings';

import { handlerDrawerOpen, useGetMenuMaster } from 'api/menu';
import { getSidebarPeek, setSidebarPeek, useSidebarPeek } from './peek';

// ==============================|| SIDEBAR DRAWER ||============================== //

function Sidebar() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const appSettings = useAppSettings();
  const settingsReady = appSettings._loaded === true;

  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const peek = useSidebarPeek();
  const peekTimer = useRef(null);

  // menu fechado: abre com o mouse em cima (pequena espera para não abrir só de passar) e fecha ao sair
  const onPeekEnter = () => {
    if (downMD || drawerOpen) return;
    clearTimeout(peekTimer.current);
    peekTimer.current = setTimeout(() => {
      setSidebarPeek(true);
      handlerDrawerOpen(true);
    }, 150);
  };
  const onPeekLeave = () => {
    clearTimeout(peekTimer.current);
    if (!getSidebarPeek()) return;
    setSidebarPeek(false);
    handlerDrawerOpen(false);
  };

  const {
    state: { menuOrientation, miniDrawer }
  } = useConfig();

  const logo = useMemo(
    () => (
      <Box sx={{ display: 'flex', p: 2 }}>
        <LogoSection />
      </Box>
    ),
    []
  );

  const drawer = useMemo(() => {
    const isVerticalOpen = menuOrientation === MenuOrientation.VERTICAL && drawerOpen;
    const drawerContent = (
      <>
        <MenuCard />
        {/* <Stack direction="row" sx={{ justifyContent: 'center', mb: 2 }}>
          <Chip label={import.meta.env.VITE_APP_VERSION} size="small" color="default" />
        </Stack> */}
      </>
    );

    const sidebarBackground = appSettings?.sidebar_grad_color
      ? `linear-gradient(135deg, ${appSettings.sidebar_grad_color}, ${appSettings.sidebar_grad_color_back || appSettings.sidebar_grad_color})`
      : undefined;
    const sidebarFontSx = appSettings?.sidebar_font_color
      ? {
          color: appSettings.sidebar_font_color,
          '& .MuiSvgIcon-root, & .MuiTypography-root, & .MuiListItemIcon-root, & .MuiListItemText-root': {
            color: appSettings.sidebar_font_color,
          },
        }
      : {};
    // Ícones do menu em "negrito": traço mais grosso (ícones são de linha)
    const iconBoldSx = {
      '& .MuiListItemIcon-root svg, & .tabler-icon': { strokeWidth: 2.25 },
    };

    let drawerSX = { paddingLeft: '0px', paddingRight: '0px', marginTop: '20px', background: sidebarBackground, ...sidebarFontSx, ...iconBoldSx };
    if (drawerOpen) drawerSX = { paddingLeft: '16px', paddingRight: '16px', marginTop: '0px', background: sidebarBackground, ...sidebarFontSx, ...iconBoldSx };

    return (
      <>
        {downMD ? (
          <Box sx={drawerSX}>
            <MenuList />
            {isVerticalOpen && drawerContent}
          </Box>
        ) : (
          <SimpleBar sx={{ height: 'calc(100vh - 90px)', ...drawerSX }}>
            <MenuList />
            {isVerticalOpen && drawerContent}
          </SimpleBar>
        )}
      </>
    );
  }, [downMD, drawerOpen, menuOrientation, appSettings]);

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 }, width: { xs: 'auto', md: drawerWidth }, visibility: settingsReady ? 'visible' : 'hidden' }} aria-label="mailbox folders">
      {downMD || (miniDrawer && drawerOpen && !peek) ? (
        <Drawer
          variant={downMD ? 'temporary' : 'persistent'}
          anchor="left"
          open={drawerOpen}
          onClose={() => handlerDrawerOpen(false)}
          slotProps={{
            paper: {
              sx: {
                mt: downMD ? 0 : 11,
                zIndex: 1099,
                width: drawerWidth,
                bgcolor: 'background.default',
                color: 'text.primary',
                borderRight: 'none'
              }
            }
          }}
          ModalProps={{ keepMounted: true }}
          color="inherit"
        >
          {downMD && logo}
          {drawer}
        </Drawer>
      ) : (
        <MiniDrawerStyled variant="permanent" open={drawerOpen} onMouseEnter={onPeekEnter} onMouseLeave={onPeekLeave}>
          {logo}
          {drawer}
        </MiniDrawerStyled>
      )}
    </Box>
  );
}

export default memo(Sidebar);